import { useCallback } from 'react';

import { useStoreCareerLessonNotesMutation } from 'core/graphql';

export interface ISaveCareerLessonNotesResult {
  success: boolean;
  /** The persisted text echoed by the server (falls back to what we sent). */
  notes: string;
}

/**
 * Autosaves the student's PRIVATE per-lesson notes (spec 0020 Фаза B).
 *
 * Called from the debounced notes panel; posts the whole text and returns the
 * persisted value. No refetch: the notes are private and the panel owns its own
 * state, seeded once from `myNotes` (re-seeded only when the lesson changes), so
 * a `cache-and-network` refetch can never clobber in-flight edits.
 *
 * A network failure propagates as a thrown ApolloError — the panel catches it
 * and shows a subtle error state without losing the student's text.
 */
export const useSaveCareerLessonNotes = () => {
  const [mutate, { loading }] = useStoreCareerLessonNotesMutation();

  const saveNotes = useCallback(
    async (lessonId: string, notes: string): Promise<ISaveCareerLessonNotesResult> => {
      const { data } = await mutate({ variables: { lessonId, notes } });
      const payload = data?.saveCareerLessonNotes;

      return {
        success: Boolean(payload?.success),
        notes: payload?.notes ?? notes,
      };
    },
    [mutate],
  );

  return { saveNotes, loading };
};
